import {
  DPComp,
  DPLayerMapping,
  DPMeta,
  RemoteDPComp,
  RemoteDPLayer,
  RemoteDPMapping,
  RemoteDPMappingDataObj,
  RemoteDPMeta,
} from './dynamicParameters.types';
import _omit from 'lodash/omit';
import _uniqBy from 'lodash/uniqBy';
import {layerByType} from './dynamicParameters.consts';
import setMappingParam from './utils/setMappingParam';
import getMappingParam from './utils/getMappingParam';
import getPropsKeyFromMapping from './utils/getPropsKeyFromMapping';
import _groupBy from 'lodash/groupBy';
import _values from 'lodash/values';
import _isNil from 'lodash/isNil';
import {SourceLayerType} from './dynamicParameters.enums';
import rgbToHex from '../../components/CLColorPicker/utils/rgbToHex';

export function getDataFromMeta(layerMeta: RemoteDPLayer) {
  const {
    id: layerId,
    parentId,
    preCompId,
    start,
    end,
    originalMediaUrl,
    textMetadata,
    width,
    height,
    fontFileUrl,
    type: layerType,
    name: layerName,
    originalText,
    fillColor,
    strokeColor,
    overlayColor,
    enabled,
  } = layerMeta;

  return {
    layerIndex: +layerId,
    originalMediaUrl,
    textMetadata: textMetadata,
    layerType,
    fontFileUrl,
    layerName: layerName || 'Untitled',
    parentId: +parentId,
    preCompId,
    start,
    end,
    width,
    height,
    enabled,
    originalText,
    originalFill:
      layerType === SourceLayerType.Text && textMetadata
        ? rgbToHex(
            textMetadata.fontColor[0] * 255,
            textMetadata.fontColor[1] * 255,
            textMetadata.fontColor[2] * 255,
          )
        : fillColor
          ? rgbToHex(...fillColor)
          : '#000000',
    originalStroke:
      layerType === SourceLayerType.Text && textMetadata?.fontStrokeColor
        ? rgbToHex(
            textMetadata.fontStrokeColor[0] * 255,
            textMetadata.fontStrokeColor[1] * 255,
            textMetadata.fontStrokeColor[2] * 255,
          )
        : strokeColor
          ? rgbToHex(...strokeColor)
          : '#000000',
    overlayColor: overlayColor ? rgbToHex(...overlayColor) : '#000000',
    isHaveOverlay: !!overlayColor,
  };
}

function toClientMapping(
  remoteMapping: RemoteDPMapping,
  remoteMeta: RemoteDPMeta,
): DPLayerMapping[] {
  const mappingList = _values(_groupBy(remoteMeta.comps, 'id'))
    .map(([{id: compId, layers}]) =>
      layers.map((layerMeta) => {
        const existingMapping = remoteMapping.dynamicMapping.find(
          ({dynamicDataObj, compId: mappingComp}) =>
            dynamicDataObj.mappingId === layerMeta.mappingId && +compId === +mappingComp,
        );

        if (existingMapping) {
          const layer: DPLayerMapping = {
            ...existingMapping.dynamicDataObj,
            conditions: existingMapping.dynamicDataObj.conditions || [],
            ...getDataFromMeta(layerMeta),
            id: `${compId}-${layerMeta.id}-${layerMeta.mappingId}`,
            compId,
          };

          layerByType[layer.layerType].params.forEach((param) => {
            const {itemIdx} = getPropsKeyFromMapping(layer, param) || {};

            if (!_isNil(itemIdx)) {
              layer.itemIndex = +itemIdx + 1;
            }
          });

          layer.conditions.forEach(({index}) => {
            layer.itemIndex = +index + 1;
          });

          return layer;
        } else {
          return null;
        }
      }),
    )
    .flat()
    .filter((t) => !!t);

  return _uniqBy(mappingList, 'id');
}

function toRemoteMapping(clientMapping: DPLayerMapping[]): RemoteDPMapping {
  return {
    dynamicMapping: clientMapping.map((layer) => {
      let layerCopy = {...layer};

      if (layerCopy.itemIndex) {
        const itemIndex = layerCopy.itemIndex;

        layerByType[layer.layerType].params.forEach(
          (param) =>
            (layerCopy = setMappingParam(
              layerCopy,
              param,
              getMappingParam(layerCopy, param)?.paramKey ?? null,
              itemIndex - 1,
            )),
        );
        layerCopy.conditions = layerCopy.conditions.map((cond) => ({
          ...cond,
          index: itemIndex - 1,
        }));
      }

      const {layerIndex, compId, id, ...dynamicDataObj} = layerCopy;

      const baseDataObj = _omit(dynamicDataObj, [
        'layerType',
        'layerName',
        'id',
        'parentId',
        'preCompId',
        'start',
        'end',
        'originalMediaUrl',
        'textMetadata',
        'width',
        'height',
        'fontFileUrl',
        'originalText',
        'originalFill',
        'originalStroke',
        'overlayColor',
        'isHaveOverlay',
      ]) as RemoteDPMappingDataObj;

      return {
        layerId: `${layerIndex}`,
        compId,
        dynamicDataObj: {
          ...baseDataObj,
          mappingId: +(id.split('-').pop() || 0),
          ...(dynamicDataObj.txtData?.parts?.[0]?.val && {txtData: dynamicDataObj.txtData}),
        },
      };
    }),
  };
}

function toClientComp(comp: RemoteDPComp): DPComp {
  return {
    ...comp,
    layers: comp.layers.map((layer) => ({
      ..._omit(layer, 'id'),
      layerIndex: +layer.id,
      name: layer.name || 'Untitled',
    })),
  };
}

function toClientMeta(meta: RemoteDPMeta): DPMeta {
  return {
    mainComp: toClientComp(meta.comps.find((comp) => comp.isMain) || meta.comps[0]),
    comps: meta.comps.map(toClientComp),
  };
}

export {toClientMapping, toRemoteMapping, toClientMeta};
